import i18n from '../i18n';

/**
 * Data adapters to normalize differences between ExperimentPanel and ProductExperimentPanelNew
 */

// Adapter for ExperimentPanel (original implementation)
export const createExperimentPanelAdapter = (machineData) => ({
  // Generate product key for ExperimentPanel: "${row}-${column}"
  generateProductKey: (slot) => `${slot.row}-${slot.column}`,
  
  // Generate machine key for ExperimentPanel: "${machineId}-${productKey}"
  generateMachineKey: (slot, productKey) => `${machineData?.machineId}-${productKey}`,
  
  // Get experiment from slot
  getExperiment: (slot) => slot.experiment,
  
  // Get product name from slot
  getProductName: (slot) => {
    if (slot?.experiment?.experimentType === 'reemplazo_producto' || slot?.experiment?.experimentType === 'ampliar_carril') {
      const originalName = slot.experiment.originalProduct?.name || slot.name;
      return originalName === slot.name ? slot.name : `${originalName} → ${slot.name}`;
    }
    return slot.name;
  },
  
  // Data source type identifier
  sourceType: 'experimentPanel'
});

// Adapter for ProductExperimentPanelNew
export const createProductExperimentPanelAdapter = () => ({
  // Generate product key for ProductExperimentPanelNew: "${machineId}-${row}-${column}"
  generateProductKey: (problem) => `${problem.machineId}-${problem.slot.row}-${problem.slot.column}`,
  
  // Generate machine key for ProductExperimentPanelNew: same as productKey
  generateMachineKey: (problem, productKey) => productKey,
  
  // Get experiment from problem
  getExperiment: (problem) => problem.experiment,
  
  // Get product name from problem
  getProductName: (problem) => problem.productName,
  
  // Data source type identifier
  sourceType: 'productExperimentPanel'
});

// Adapter for ExperimentManagement
export const createExperimentManagementAdapter = () => ({
  // Generate product key for ExperimentManagement: experiment.id
  generateProductKey: (experiment) => experiment.id,
  
  // Generate machine key for ExperimentManagement: "${machineId}-${id}"
  generateMachineKey: (experiment, productKey) => `${experiment.machineId}-${experiment.id}`,
  
  // Get experiment from experiment object (nested structure)
  getExperiment: (experiment) => experiment.experiment,
  
  // Get product name from experiment
  getProductName: (experiment) => experiment.productName,
  
  // Data source type identifier
  sourceType: 'experimentManagement'
});

/**
 * Normalize experiment data for consistent processing
 * @param {Array} data - Raw data array (slots or problems)
 * @param {Object} adapter - Data adapter for the specific component
 * @returns {Array} Normalized experiment data
 */
export const normalizeExperimentData = (data, adapter) => {
  if (!data || !Array.isArray(data)) return [];
  
  return data.map(item => ({
    original: item,
    productKey: adapter.generateProductKey(item),
    machineKey: adapter.generateMachineKey(item, adapter.generateProductKey(item)),
    experiment: adapter.getExperiment(item),
    productName: adapter.getProductName(item),
    sourceType: adapter.sourceType
  }));
};

/**
 * Generate product data map with API response overrides support
 * @param {Array} normalizedData - Normalized experiment data
 * @param {Object} customDiscounts - Custom discount state
 * @param {Object} apiResponseOverrides - API response override state
 * @param {Function} getEffectiveDiscount - Function to get effective discount
 * @param {Function} calculateDiscountedPrice - Function to calculate discounted price
 * @param {Function} t - Translation function
 * @returns {Object} Product data map
 */
export const generateProductDataMap = (
  normalizedData, 
  customDiscounts, 
  apiResponseOverrides, 
  getEffectiveDiscount,
  calculateDiscountedPrice,
  t
) => {
  const productDataMap = {};
  
  normalizedData.forEach(({ original, productKey, experiment, productName }) => {
    // Use real experiment data if available
    if (experiment && (
      experiment.experimentType === 'promo_combo_combinado' || 
      experiment.experimentType === 'promo_precio_unitario' ||
      experiment.experimentType === 'alza_precio_sugerido' ||
      experiment.experimentType === 'reemplazo_producto' ||
      experiment.experimentType === 'ampliar_carril'
    )) {
      const exp = experiment;
      
      // Get margin and sales data based on source type
      let productMargin, machineAvgMargin, productAvgSales, machineAvgSales;
      
      if (original.productMargin !== undefined) {
        // ExperimentPanel format
        productMargin = original.productMargin || 0;
        machineAvgMargin = original.machineAvgMargin || 0;
        productAvgSales = original.productAvgSales || 0;
        machineAvgSales = original.machineAvgSales || 0;
      } else {
        // ProductExperimentPanelNew format - extract from problem data
        productMargin = original.productMargin || 0;
        machineAvgMargin = original.machineAvgMargin || 0;
        productAvgSales = original.productAvgSales || 0;
        machineAvgSales = original.machineAvgSales || 0;
      }
      
      // Build candidates array based on experiment type
      let candidates = [];
      
      
      if (exp.experimentType === 'reemplazo_producto' || exp.experimentType === 'ampliar_carril') {
        // For replacement/lane-expansion experiments, use candidateNames (no pricing)
        candidates = (exp.candidateNames || []).map(candidateName => ({
          name: candidateName
          // No price for replacement experiments
        }));
      } else if (exp.experimentType === 'alza_precio_sugerido') {
        // Price increases apply to the same product: there is nothing to choose from
        candidates = [];
      } else if (exp.comboPrices) {
        // For combo/discount experiments, use comboPrices with pricing
        candidates = exp.comboPrices.map(combo => {
          // Check for API response overrides first
          const overrideKey = `${productKey}-${combo.candidateName}`;
          if (apiResponseOverrides[overrideKey]) {
            return {
              name: combo.candidateName,
              price: apiResponseOverrides[overrideKey].price.toFixed(2)
            };
          }
          
          // Use API price unless user modified discount
          const comboForDiscount = { name: combo.candidateName };
          const effectiveDiscount = getEffectiveDiscount(productKey, original, comboForDiscount);
          
          let dynamicPrice;
          if (customDiscounts[productKey] !== undefined) {
            // User modified - calculate new price
            dynamicPrice = calculateDiscountedPrice(combo.totalWithoutDiscount || 0, effectiveDiscount);
          } else {
            // Use API value with fallback
            dynamicPrice = combo.totalWithDiscount || 0;
          }
          
          return {
            name: combo.candidateName,
            price: (dynamicPrice || 0).toFixed(2)
          };
        });
      }


      productDataMap[productKey] = {
        avgSales: exp.previousUnitsSold || 5,
        kpiString: `€${productMargin} ${t('margin')} (${t('vsMachine')} €${machineAvgMargin})\n${productAvgSales} ${t('averageUnitsSold')} (${t('vsMachine')} ${machineAvgSales})`,
        candidates,
        startDate: new Date(exp.dateFrom).toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { day: 'numeric', month: 'long' }),
        endDate: new Date(exp.dateTo).toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { day: 'numeric', month: 'long' })
      };
    } else {
      // Fallback for non-combo experiments or critical items
      let productMargin, machineAvgMargin, productAvgSales, machineAvgSales;
      
      if (original.productMargin !== undefined) {
        productMargin = original.productMargin || 0;
        machineAvgMargin = original.machineAvgMargin || 0;
        productAvgSales = original.productAvgSales || 0;
        machineAvgSales = original.machineAvgSales || 0;
      } else {
        productMargin = 0;
        machineAvgMargin = 0;
        productAvgSales = 0;
        machineAvgSales = 0;
      }
      
      productDataMap[productKey] = {
        avgSales: 5,
        kpiString: `€${productMargin} ${t('margin')} (${t('vsMachine')} €${machineAvgMargin})\n${productAvgSales} ${t('averageUnitsSold')} (${t('vsMachine')} ${machineAvgSales})`,
        candidates: [],
        startDate: '',
        endDate: ''
      };
    }
  });
  
  return productDataMap;
};
